import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  UserCheck, Search, Loader2, CheckCircle, XCircle, DollarSign, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FALLBACK_TRIPS } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function TravellingAdminManager() {
  const [pendingClaims, setPendingClaims] = useState([]);
  const [pendingRecons, setPendingRecons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("claims");
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [notes, setNotes] = useState("");

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const [claimsRes, reconsRes] = await Promise.all([
        axios.get(`${API}/travelling/requests?status=claim_submitted`, { headers: getHeaders() }),
        axios.get(`${API}/travelling/requests?status=recon_ai_verified`, { headers: getHeaders() }),
      ]);
      setPendingClaims(claimsRes.data);
      setPendingRecons(reconsRes.data);
    } catch {
      const claimTrips = FALLBACK_TRIPS.filter(t => t.status === "claim_submitted" || t.status === "manager_recommended");
      const reconTrips = FALLBACK_TRIPS.filter(t => t.status === "recon_submitted" || t.status === "recon_ai_verified");
      setPendingClaims(claimsRes?.data?.length > 0 ? claimsRes.data : claimTrips);
      setPendingRecons(reconsRes?.data?.length > 0 ? reconsRes.data : reconTrips);
      toast.error("Failed to load. Showing demo data.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const openDetail = async (trip, type) => {
    setActiveTab(type);
    setNotes("");
    try {
      if (type === "claims") {
        const res = await axios.get(`${API}/travelling/claims?request_id=${trip.id}`, { headers: getHeaders() });
        setSelected({ trip, claim: res.data[0] || null, type: "claims" });
      } else {
        const res = await axios.get(`${API}/travelling/reconciliations?request_id=${trip.id}`, { headers: getHeaders() });
        setSelected({ trip, recon: res.data[0] || null, type: "recons" });
      }
      setDetailOpen(true);
    } catch { toast.error("Failed to load details"); }
  };

  const handleRecommend = async (recommendation) => {
    if (!selected) return;
    setLoading(true);
    try {
      if (selected.type === "claims") {
        await axios.put(
          `${API}/travelling/claims/${selected.claim.id}/manager-recommend?recommendation=${recommendation}&notes=${encodeURIComponent(notes)}`,
          {}, { headers: getHeaders() }
        );
        toast.success(`Claim ${recommendation}`);
      } else {
        await axios.put(
          `${API}/travelling/reconciliations/${selected.recon.id}/manager-recommend?recommendation=${recommendation}&notes=${encodeURIComponent(notes)}`,
          {}, { headers: getHeaders() }
        );
        toast.success(`Reconciliation ${recommendation}`);
      }
      setDetailOpen(false);
      fetchPending();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
            <UserCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">MANAGER</h1>
            <p className="text-slate-600">Recommend expense claims & reconciliations</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button onClick={() => setActiveTab("claims")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "claims" ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}>
          Pending Claims ({pendingClaims.length})
        </button>
        <button onClick={() => setActiveTab("recons")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "recons" ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-700"
          }`}>
          Pending Reconciliations ({pendingRecons.length})
        </button>
      </div>

      {loading && activeTab === "claims" ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : activeTab === "claims" ? (
        pendingClaims.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
            <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No pending claims to review.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-app border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-600">Employee</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Destination</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Period</th>
                  <th className="text-left p-3 font-semibold text-slate-600"></th>
                </tr>
              </thead>
              <tbody>
                {pendingClaims.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-app">
                    <td className="p-3 font-medium text-slate-800">{t.employee_name}</td>
                    <td className="p-3 text-slate-700">{t.destination}</td>
                    <td className="p-3 text-xs text-slate-500">{t.start_date?.slice(0,10)} → {t.end_date?.slice(0,10)}</td>
                    <td className="p-3">
                      <Button size="sm" onClick={() => openDetail(t, "claims")} className="rounded-none bg-sky-600 hover:bg-sky-500 text-white">
                        Review Claim
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : activeTab === "recons" ? (
        pendingRecons.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No pending reconciliations to review.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-app border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-600">Employee</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Destination</th>
                  <th className="text-left p-3 font-semibold text-slate-600">AI Verdict</th>
                  <th className="text-left p-3 font-semibold text-slate-600"></th>
                </tr>
              </thead>
              <tbody>
                {pendingRecons.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-app">
                    <td className="p-3 font-medium text-slate-800">{t.employee_name}</td>
                    <td className="p-3 text-slate-700">{t.destination}</td>
                    <td className="p-3">
                      <span className="text-xs px-2 py-0.5 text-amber-600 bg-amber-50">AI Verified</span>
                    </td>
                    <td className="p-3">
                      <Button size="sm" onClick={() => openDetail(t, "recons")} className="rounded-none bg-sky-600 hover:bg-sky-500 text-white">
                        Review Recon
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {/* Review Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl">
              {selected?.type === "claims" ? "Review Expense Claim" : "Review Reconciliation"}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 mt-2">
              <div className="text-sm space-y-1 bg-app p-3 border border-slate-200">
                <p><span className="text-slate-500">Employee:</span> <strong>{selected.trip.employee_name}</strong></p>
                <p><span className="text-slate-500">Destination:</span> {selected.trip.destination}</p>
                <p><span className="text-slate-500">Period:</span> {selected.trip.start_date?.slice(0,10)} → {selected.trip.end_date?.slice(0,10)}</p>
              </div>

              {selected.type === "claims" && selected.claim && (
                <div className="bg-purple-50 p-3 border border-purple-200 text-sm space-y-1">
                  <p><span className="text-slate-500">GL Account:</span> {selected.claim.gl_account}</p>
                  <p><span className="text-slate-500">Daily S&T:</span> {selected.claim.daily_s_and_t_amount}</p>
                  <p><span className="text-slate-500">Total:</span> {selected.claim.total_claim_amount}</p>
                </div>
              )}

              {selected.type === "recons" && selected.recon && (
                <div className="bg-orange-50 p-3 border border-orange-200 text-sm space-y-1">
                  <p><span className="text-slate-500">Amount Used:</span> {selected.recon.total_amount_used}</p>
                  <p><span className="text-slate-500">Amount Returned:</span> {selected.recon.total_amount_returned}</p>
                  <p><span className="text-slate-500">Over:</span> <span className="text-green-600">{selected.recon.over_amount}</span></p>
                  <p><span className="text-slate-500">Under:</span> <span className="text-red-600">{selected.recon.under_amount}</span></p>
                  {selected.recon.ai_verification_notes && (
                    <p className="text-xs text-slate-500 mt-2 italic">AI: {selected.recon.ai_verification_notes}</p>
                  )}
                </div>
              )}

              <textarea placeholder="Notes / reason for decision" value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />

              <div className="flex gap-2">
                <Button onClick={() => handleRecommend("recommended")} disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-500 rounded-none text-white">
                  <CheckCircle className="w-4 h-4 mr-1" /> Recommend
                </Button>
                <Button onClick={() => handleRecommend("declined")} disabled={loading}
                  variant="outline" className="flex-1 rounded-none border-red-300 text-red-600">
                  <XCircle className="w-4 h-4 mr-1" /> Decline
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
